import LegalPage from "@/pages/LegalPage";

const SECTION_HEADING = "font-display text-xl md:text-2xl font-bold text-white tracking-tight mt-10 mb-3";
const PARAGRAPH = "text-[#CBD5E1] leading-relaxed text-[15px] md:text-base";
const LIST = "list-disc pl-6 mt-3 space-y-2 text-[#CBD5E1] text-[15px] leading-relaxed marker:text-[#F59E0B]";

export default function TermsOfService() {
  return (
    <LegalPage
      testid="terms-of-service-page"
      title="Terms of Service"
      subtitle="The rules of the road for using tweakmycreditreport.com and any products you buy from us. Read this carefully before purchasing."
      updated="December 2025"
    >
      {/* Prominent disclaimer */}
      <div
        className="rounded-xl p-6 md:p-7 mb-10"
        style={{
          background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))",
          border: "1px solid rgba(245,158,11,0.4)",
        }}
        data-testid="terms-prominent-disclaimer"
      >
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FCD34D] mb-3">
          Important Disclaimer
        </div>
        <p className="text-[#F8FAFC] font-semibold leading-relaxed text-[15px] md:text-base">
          We are not a credit repair organization, attorneys, or financial advisors. All content on
          this website and in the products we sell is provided for <em>educational and informational
          purposes only</em> and does not constitute legal, financial, tax, or credit-counseling
          advice. Results vary based on your individual circumstances. You are solely responsible for
          any action you take based on this information.
        </p>
      </div>

      <p className={PARAGRAPH}>
        These Terms of Service ("Terms") form a binding agreement between you and
        tweakmycreditreport.com ("we," "us," or "our") governing your use of the website and any
        products, templates, guides, or other digital materials (the "Products") you purchase. By
        using the site or purchasing a Product, you agree to these Terms in full. If you do not
        agree, do not use the site or purchase the Products.
      </p>

      <h2 className={SECTION_HEADING}>1. No Professional Relationship</h2>
      <p className={PARAGRAPH}>
        Nothing on this site or in any Product creates an attorney-client, fiduciary, financial-
        advisory, or credit-counseling relationship between you and us. We are <strong className="text-white">not</strong>{" "}
        a Credit Repair Organization ("CRO") within the meaning of the federal Credit Repair
        Organizations Act (CROA), 15 U.S.C. § 1679 et seq., and we do not perform credit-repair
        services on your behalf. If you need individualized advice, consult a licensed attorney,
        accountant, HUD-approved counselor, or financial advisor.
      </p>

      <h2 className={SECTION_HEADING}>2. Educational Purpose Only</h2>
      <p className={PARAGRAPH}>
        The Products — including guides, workbooks, and letter templates — are educational tools
        designed to help consumers exercise rights they already have under the Fair Credit Reporting
        Act (FCRA), the Fair Debt Collection Practices Act (FDCPA), and related laws. We make no
        guarantees, warranties, or representations regarding any specific outcome, including but not
        limited to:
      </p>
      <ul className={LIST}>
        <li>Any particular increase in your credit score.</li>
        <li>Removal of any specific item from any credit report.</li>
        <li>Approval of any loan, credit line, mortgage, or rental application.</li>
        <li>Time required to see results (results vary by individual and circumstance).</li>
      </ul>

      <h2 className={SECTION_HEADING}>3. License & Personal Use Only</h2>
      <p className={PARAGRAPH}>
        When you purchase a Product, we grant you a non-exclusive, non-transferable, revocable
        license to use the Product for your <strong className="text-white">personal, non-commercial
        use only</strong>. You may not:
      </p>
      <ul className={LIST}>
        <li>Resell, redistribute, or publicly share the Product or its contents.</li>
        <li>Post the Product (in whole or part) to file-sharing sites, forums, or cloud drives.</li>
        <li>Use the Product or templates to provide credit-repair services to other consumers
          (including paid or unpaid coaching, consulting, or "done-for-you" services).</li>
        <li>Create derivative works, courses, or products based on the Product.</li>
        <li>Remove or alter copyright, trademark, or attribution notices.</li>
      </ul>
      <p className={`${PARAGRAPH} mt-4`}>
        All intellectual property in the Products — text, design, letter templates, workflows, and
        branding — remains the sole property of tweakmycreditreport.com.
      </p>

      <h2 className={SECTION_HEADING}>4. Rules for Template Use</h2>
      <p className={PARAGRAPH}>
        The dispute letter templates included in the Products are a powerful tool and must be used
        responsibly and lawfully. By using them, you agree that:
      </p>
      <ul className={LIST}>
        <li>
          <strong className="text-white">You will only dispute items you have a good-faith basis to
          believe are inaccurate, unverifiable, incomplete, or obsolete.</strong>
        </li>
        <li>
          <strong className="text-white">You will not submit false, fraudulent, or misleading
          statements</strong> to any credit bureau, furnisher, creditor, or other party. Knowingly
          making false statements in a dispute may constitute fraud, mail fraud, wire fraud, or other
          criminal or civil violations.
        </li>
        <li>
          You will not use the templates to dispute items solely to harass a creditor, evade a
          legitimate debt you owe, or delay lawful collection.
        </li>
        <li>
          You will not impersonate another person, forge signatures, or use another person's
          personal information.
        </li>
        <li>
          You will comply with all applicable federal, state, and local laws, including the FCRA,
          FDCPA, and CROA (as applicable).
        </li>
      </ul>
      <p className={`${PARAGRAPH} mt-4`}>
        You alone are responsible for the accuracy and legality of any dispute, letter, or claim you
        submit. We disclaim any liability for misuse of the templates.
      </p>

      <h2 className={SECTION_HEADING}>5. Payment, Digital Delivery & No Refunds</h2>
      <p className={PARAGRAPH}>
        All prices are in U.S. dollars and are final at the price shown at checkout. Payment is
        processed through PayPal and collected in full at the time of purchase.
      </p>
      <p className={`${PARAGRAPH} mt-4`}>
        Because our Products are digital goods delivered instantly upon purchase,{" "}
        <strong className="text-white">all sales are final and we do not offer refunds,
        cancellations, or exchanges</strong>, in whole or in part, under any circumstances except
        where required by applicable law. Please read the product description, sample content, and
        this Terms of Service carefully before completing your purchase.
      </p>
      <p className={`${PARAGRAPH} mt-4`}>
        Chargebacks, payment disputes, or unauthorized reversals filed without first contacting us in
        good faith may result in suspension of your license to use the Product and forwarding of the
        matter to a collection agency.
      </p>

      <h2 className={SECTION_HEADING}>6. Acceptable Use of the Website</h2>
      <p className={PARAGRAPH}>You agree not to:</p>
      <ul className={LIST}>
        <li>Attempt to gain unauthorized access to any portion of the site, servers, or systems.</li>
        <li>Use automated tools (bots, scrapers) to harvest content, email addresses, or testimonials.</li>
        <li>Interfere with or disrupt the site or associated services.</li>
        <li>Submit false information to our lead forms or checkout.</li>
        <li>Use the site or any Product for any unlawful purpose.</li>
      </ul>

      <h2 className={SECTION_HEADING}>7. Disclaimer of Warranties</h2>
      <p className={PARAGRAPH}>
        The site and all Products are provided <strong className="text-white">"as is" and "as
        available,"</strong> without warranties of any kind, express or implied, including without
        limitation implied warranties of merchantability, fitness for a particular purpose, accuracy,
        completeness, non-infringement, or title. We do not warrant that the site will be
        uninterrupted, error-free, or free of viruses or other harmful components.
      </p>

      <h2 className={SECTION_HEADING}>8. Limitation of Liability</h2>
      <p className={PARAGRAPH}>
        To the maximum extent permitted by law, in no event will tweakmycreditreport.com, its
        owners, employees, contractors, or affiliates be liable to you for any indirect, incidental,
        consequential, special, exemplary, or punitive damages — including but not limited to lost
        profits, lost credit opportunities, loan denials, or emotional distress — arising out of or
        related to your use of the site or any Product, even if advised of the possibility of such
        damages. Our total aggregate liability to you for any claim shall not exceed the amount you
        actually paid us for the Product giving rise to the claim.
      </p>

      <h2 className={SECTION_HEADING}>9. Indemnification</h2>
      <p className={PARAGRAPH}>
        You agree to defend, indemnify, and hold harmless tweakmycreditreport.com and its owners,
        employees, contractors, and affiliates from and against any claim, loss, demand, or expense
        (including reasonable attorneys' fees) arising out of or related to: (a) your misuse of any
        Product or template; (b) your breach of these Terms; or (c) your violation of any law or the
        rights of any third party.
      </p>

      <h2 className={SECTION_HEADING}>10. Governing Law & Dispute Resolution</h2>
      <p className={PARAGRAPH}>
        These Terms are governed by the laws of the United States and the state in which
        tweakmycreditreport.com's owner resides, without regard to conflict-of-laws principles. You
        and we agree to first attempt to resolve any dispute informally by contacting us at{" "}
        <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
          info@tweakmycreditreport.com
        </a>
        . If the dispute is not resolved within 60 days, either party may pursue the dispute in a
        court of competent jurisdiction.
      </p>

      <h2 className={SECTION_HEADING}>11. Changes to These Terms</h2>
      <p className={PARAGRAPH}>
        We may update these Terms from time to time. The revised version will be posted here with an
        updated "Last updated" date. Continued use of the site or Products after a change
        constitutes acceptance of the updated Terms.
      </p>

      <h2 className={SECTION_HEADING}>12. Contact</h2>
      <p className={PARAGRAPH}>
        Questions about these Terms? Contact us at{" "}
        <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
          info@tweakmycreditreport.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
